/**
 * EmptyDataState
 * =================
 * Shown by every authed feature page when the user has no connected
 * data yet. Three CTAs:
 *
 *   1. Connect Your Cloud  — primary, real-data path
 *   2. Try with sample data — secondary, hits /api/onboarding/seed-sample
 *      so the user can explore the full surface in one click
 *   3. Or see what this looks like with data — link to landing demo
 *
 * Sample-data CTA is hidden when sample data is already loaded (the
 * caller decides which page to render at that point).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Cloud, ArrowRight, Sparkles, Lock, Wand2, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmptyDataState = ({
  title = 'No Data Yet',
  description = 'Connect your cloud providers to see real data and insights here.',
  icon: Icon = Cloud,
  ctaLabel = 'Connect Your Cloud Account',
  ctaPath = '/cloud-audit',
  /** When true, hide the "Try with sample data" CTA. Use on pages that
   *  already render with seeded data when the org has none. */
  hideSampleSeed = false,
}) => {
  const [seeding, setSeeding] = useState(false);
  const [seeded, setSeeded] = useState(false);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const tok = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/onboarding/seed-sample`,
        {},
        { headers: tok ? { Authorization: `Bearer ${tok}` } : {} },
      );
      const s = data?.seeded || {};
      const counts = [
        s.customer_pl ? `${s.customer_pl} customers` : null,
        s.ai_features ? `${s.ai_features} AI features` : null,
        s.k8s_clusters ? `${s.k8s_clusters} K8s clusters` : null,
      ].filter(Boolean).join(' · ');
      toast.success(`Sample data seeded${counts ? `: ${counts}` : ''} — refreshing…`);
      setSeeded(true);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not seed sample data — please try again.');
      setSeeding(false);
    }
  };

  return (
    <div className="px-6 py-10 sm:py-16">
      <div className="max-w-2xl mx-auto" data-testid="empty-data-state">
        {/* Page header — gives the page presence even when empty */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <Icon className="w-5 h-5 text-emerald-500" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
            {title}
          </h1>
        </div>

        {/* Empty-state card */}
        <div className="relative mt-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 p-8 sm:p-10 overflow-hidden">
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />

          <div className="relative text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/25 mb-5">
              <Icon className="w-7 h-7 text-emerald-500" />
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-3">
              Connect a cloud to unlock {title}
            </h2>
            <p className="text-sm sm:text-base text-slate-400 dark:text-slate-400 leading-relaxed max-w-lg mx-auto mb-6">
              {description}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to={ctaPath}>
                <Button
                  size="lg"
                  className="bg-emerald-600 hover:bg-emerald-700 text-base px-6 group"
                  data-testid="connect-cloud-account-btn"
                >
                  {ctaLabel}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </Link>

              {!hideSampleSeed && (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleSeed}
                  disabled={seeding || seeded}
                  className="border-emerald-400/40 text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-200 hover:bg-emerald-500/10"
                  data-testid="seed-sample-data-btn"
                >
                  {seeding ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Seeding…</>
                    : seeded ? <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Seeded</>
                    : <><Wand2 className="w-4 h-4 mr-1.5" /> Try with sample data</>}
                </Button>
              )}
            </div>

            <Link
              to="/landing#killer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 hover:text-emerald-600 transition-colors mt-4"
              data-testid="see-demo-link"
            >
              <Sparkles className="w-4 h-4" />
              Or see what this looks like with data
            </Link>

            <div className="mt-6 inline-flex items-center gap-2 text-xs text-slate-400">
              <Lock className="w-3.5 h-3.5 text-emerald-500" />
              Read-only access · We can't modify, delete, or create resources · Takes 60 seconds
            </div>
          </div>
        </div>

        <div className="mt-8 grid sm:grid-cols-3 gap-3 text-sm">
          {[
            { icon: '⚡', text: 'Real-time spend across every cloud' },
            { icon: '🤖', text: 'AI explanations for every spike' },
            { icon: '💰', text: 'One-click savings actions' },
          ].map((b, i) => (
            <div key={i} className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-4 flex items-start gap-3">
              <span className="text-lg flex-shrink-0">{b.icon}</span>
              <span className="text-slate-700 dark:text-slate-300 leading-snug">{b.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmptyDataState;
